import React, { useState, useEffect, useCallback } from "react";
import {
    Table,
    Row,
    Col,
    Input,
    Select,
    DatePicker,
    Tag,
    Card,
    Button,
    Space,
    Typography,
    Tooltip,
    Drawer,
    Badge,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Key } from "antd/es/table/interface";
import { getUserActivityLogs, getActivityLogActors } from "@/ts/retrieve-data";
import { showToast } from "@/ts/modals";
import { ACTIVITY_TYPES } from "@/ts/common-types";
import { convertIsoDate } from "@/ts/utils";
import { useUserTimezone } from "@/hooks/AllCustomHooks";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "@/ts/constants";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

interface ActivityLogsProps {}

interface ActivityLog {
    id: number;
    actor_user_id: number | null;
    actor_name: string;
    actor_email: string;
    activity_type: string;
    activity_subtype: string;
    change_description: string;
    target_entity_type: string | null;
    target_entity_id: string | null;
    target_entity_name: string | null;
    metadata: Record<string, unknown> | null;
    created_at: string;
    tagtype?: string | null;
}

interface Filters {
    activityTypes: string[];
    startDate: string | null;
    endDate: string | null;
    changedBy: string | null;
    search: string;
}

const ActivityLogs: React.FC<ActivityLogsProps> = () => {
    const currentTimeZone = useUserTimezone();

    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
    });

    const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
    const [filters, setFilters] = useState<Filters>({
        activityTypes: [],
        startDate: null,
        endDate: null,
        changedBy: null,
        search: "",
    });
    const [actors, setActors] = useState<Array<{ actor_user_id: number | null; actor_name: string; actor_email: string }>>([]);

    const fetchLogs = useCallback(
        async (page = 1, pageSize = 50, currentFilters = filters) => {
            setLoading(true);
            try {
                const apiFilters: {
                    activityTypes?: string[];
                    startDate?: string;
                    endDate?: string;
                    search?: string;
                    actorName?: string;
                } = {};

                if (currentFilters.activityTypes.length > 0) {
                    apiFilters.activityTypes = currentFilters.activityTypes;
                }
                if (currentFilters.startDate) {
                    apiFilters.startDate = currentFilters.startDate;
                }
                if (currentFilters.endDate) {
                    apiFilters.endDate = currentFilters.endDate;
                }
                if (currentFilters.search && currentFilters.search.trim() !== '') {
                    apiFilters.search = currentFilters.search.trim();
                }
                if (currentFilters.changedBy !== null && currentFilters.changedBy !== undefined) {
                    apiFilters.actorName = currentFilters.changedBy;
                }
                const result = await getUserActivityLogs(apiFilters, page, pageSize);
                let logs = result.logs || [];
                logs = logs.map((log: ActivityLog) => {
                    if (log.metadata && typeof log.metadata === 'string') {
                        try {
                            log.metadata = JSON.parse(log.metadata) as Record<string, unknown>;
                        } catch (e) {
                            console.warn('Failed to parse metadata for log:', log.id, e);
                            log.metadata = null;
                        }
                    }                        
                    return log;
                });
                
                const totalFromBackend = result.pagination?.total || 0;
                setLogs(logs);
                setPagination({
                    current: page,
                    pageSize,
                    total: totalFromBackend,
                });
            } catch (error) {
                showToast("danger", "Failed to load activity logs");
            } finally {
                setLoading(false);
            }
        },
        [filters]
    );

    useEffect(() => {
        fetchLogs(1, pagination.pageSize, filters);
        const fetchActors = async () => {
            try {
                const response = await getActivityLogActors();
                console.log("Actors response from getActivityLogActors:", response);
                console.log("Is array?", Array.isArray(response));
                console.log("Response type:", typeof response);
                
                if (Array.isArray(response)) {
                    console.log("Setting actors array with length:", response.length);
                    setActors(response);
                } else if (response && typeof response === 'object' && response.data && Array.isArray(response.data)) {
                    console.log("Setting actors from response.data with length:", response.data.length);
                    setActors(response.data);
                } else {
                    console.warn("Unexpected response format:", response);
                }
            } catch (error) {
                console.error("Failed to fetch activity log actors:", error);
            }
        };
        fetchActors();
    }, []);

    const getChangeTypeColor = (type: string): string => {
        const colorMap: Record<string, string> = {
            USER_MANAGEMENT: "blue",
            CLIENT_MANAGEMENT: "cyan",
            ROLE_MANAGEMENT: "purple",
            ROLE_PERMISSION_MANAGEMENT: "geekblue",
            USER_PRIVILEGE_MANAGEMENT: "gold",
            REPORT_MANAGEMENT: "green",
            FEATURE_MANAGEMENT: "orange",
            USER_PREFERENCES: "lime",
            BUSINESS_LOGIC: "magenta",
            TAG_MANAGEMENT: "volcano",
        };
        return colorMap[type] || "default";
    };

    const getActorType = (record: ActivityLog): "system" | "admin" | "user" => {
        if (record.actor_name === "System" || !record.actor_user_id) {
            return "system";
        }
        return "user";
    };

    const getActorStyle = (actorType: "system" | "admin" | "user") => {
        const styleMap = {
            system: {
                backgroundColor: "#FFF9C4",
                color: "#F57F17",
                border: "none",
            },
            admin: {
                backgroundColor: "#FCE4EC",
                color: "#C2185B",
                border: "none",
            },
            user: {
                backgroundColor: "#E0F2F1",
                color: "#00796B",
                border: "none",
            },
        };
        return styleMap[actorType] || {
            backgroundColor: "#F5F5F5",
            color: "#666",
            border: "none",
        };
    };

    const formatChangeType = (type: string): string => {
        return type
            .split("_")
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(" ");
    };

    const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        setFilters({
            ...filters,
            startDate: dates?.[0]?.toISOString() || null,
            endDate: dates?.[1]?.toISOString() || null,
        });
    };

    const applyFilters = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchLogs(1, pagination.pageSize, filters);
        setFilterDrawerVisible(false);
    };

    const resetFilters = () => {
        const activeCount = getActiveFilterCount();
        if (activeCount === 0) {
            setFilterDrawerVisible(false);
            return;
        }

        const resetFiltersState: Filters = {
            activityTypes: [],
            startDate: null,
            endDate: null,
            changedBy: null,
            search: "",
        };
        setFilters(resetFiltersState);
        // Reset to page 1 when resetting filters and fetch fresh data
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchLogs(1, pagination.pageSize, resetFiltersState);
        setFilterDrawerVisible(false);
    };

    const getActiveFilterCount = (): number => {
        let count = 0;
        if (filters.activityTypes && filters.activityTypes.length > 0) {
            count++;
        }
        if (filters.startDate && filters.endDate) {
            count++;
        }
        if (filters.changedBy !== null && filters.changedBy !== undefined) {
            count++;
        }
        if (filters.search && filters.search.trim() !== '') {
            count++;
        }
        return count;
    };

    const columns: ColumnsType<ActivityLog> = [
        {
            title: "DATE",
            dataIndex: "created_at",
            key: "date",
            width: 120,
            sorter: (a: ActivityLog, b: ActivityLog) =>
                new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
            defaultSortOrder: "descend" as const,
            fixed: "left" as const,
            render: (text: string) => {
                const date = dayjs(text);
                return date.format("DD MMM YY");
            },
        },
        {
            title: "TIME",
            dataIndex: "created_at",
            key: "time",
            width: 80,
            render: (text: string) => {
                const date = dayjs(text);
                return date.format("HH:mm");
            },
        },
        {
            title: "CHANGE TYPE",
            dataIndex: "activity_type",
            key: "changeType",
            width: 160,
            render: (type: string) => (
                <Tag color={getChangeTypeColor(type)}>{formatChangeType(type)}</Tag>
            ),
        },
        {
            title: "DESCRIPTION",
            key: "description",
            width: 300,
            render: (_: unknown, record: ActivityLog) => {
                const maxLength = 70;
                const description = record.change_description || "";
                const isTruncated = description.length > maxLength;
                const truncatedText = isTruncated 
                    ? description.substring(0, maxLength) + "..." 
                    : description;

                return (
                <div>
                        <Tooltip title={isTruncated ? description : null}>
                            <div style={{ fontWeight: 500, marginBottom: 4, cursor: isTruncated ? "help" : "default" }}>
                                {truncatedText}
                    </div>
                        </Tooltip>
                    {record.activity_subtype && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.activity_subtype}
                        </Text>
                    )}
                </div>
                );
            },
        },
        {
            title: "CHANGED BY",
            key: "changedBy",
            width: 160,
            render: (_: unknown, record: ActivityLog) => {
                const actorType = getActorType(record);
                const emoji =
                    actorType === "system"
                        ? "⚙️"
                        : actorType === "admin"
                        ? "👨‍💼"
                        : "👤";
                const displayName =
                    record.actor_name === "System"
                        ? "System"
                        : record.actor_name || "Unknown";
                return (
                    <Tag style={getActorStyle(actorType)}>
                        {emoji} {displayName}
                    </Tag>
                );
            },
        },
    ];

    return (
        <div style={{ 
            padding: 25, 
            backgroundColor: "#f5f5f5", 
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
        }}>
            <Card
                bordered={false}
                style={{ 
                    borderRadius: 8, 
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    display: "flex",
                    flexDirection: "column",
                    height: "calc(100vh - 50px)",
                    overflow: "hidden"
                }}
                bodyStyle={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    padding: "20px",
                    overflow: "hidden",
                    height: "100%"
                }}
            >
                <div style={{ marginBottom: 20, flexShrink: 0 }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space size="large">
                                <Typography.Title
                                    level={3}
                                    style={{ margin: 0, color: "#5C6AC4" }}
                                >
                                    Activity Logs
                                </Typography.Title>
                            </Space>
                        </Col>
                        <Col>
                            <Space>
                                <Badge count={getActiveFilterCount()} showZero={false}>
                                <Button
                                    onClick={() => setFilterDrawerVisible(true)}
                                    style={{ color: "#5C6AC4", borderColor: "#5C6AC4" }}
                                >
                                    🔍 Filters
                                </Button>
                                </Badge>
                                <Button
                                    type="link"
                                    onClick={resetFilters}
                                    style={{ color: "#5C6AC4" }}
                                >
                                    Reset Filters
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>

                <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <Table
                    columns={columns}
                    dataSource={logs}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: (page, newPageSize) => {
                            const finalPageSize = newPageSize || pagination.pageSize;
                            setPagination(prev => ({ 
                                ...prev, 
                                current: page,
                                pageSize: finalPageSize 
                            }));
                            fetchLogs(page, finalPageSize, filters);
                        },
                        onShowSizeChange: (current, size) => {
                            setPagination(prev => ({ 
                                ...prev, 
                                pageSize: size, 
                                current: 1 
                            }));
                            fetchLogs(1, size, filters);
                        },
                        showSizeChanger: true,
                        pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                        showTotal: (total, range) =>
                            `${range[0]} to ${range[1]} of ${total}`,
                        showQuickJumper: true,
                        position: ['bottomCenter'],
                        style: { marginTop: 16 },
                        hideOnSinglePage: false,
                    }}
                        scroll={{ 
                            x: 1500, 
                            y: 'calc(100vh - 380px)',
                            scrollToFirstRowOnChange: true
                        }}
                    size="middle"
                    bordered={false}
                        style={{ 
                            flex: 1,
                            display: "flex",
                            flexDirection: "column"
                        }}
                    />
                        </div>

                <Drawer
                    title="Filter Activity Logs"
                    placement="right"
                    onClose={() => setFilterDrawerVisible(false)}
                    open={filterDrawerVisible}
                    width={400}
                    footer={
                        <div style={{ textAlign: "right" }}>
                            <Space>
                                <Button onClick={resetFilters}>Reset</Button>
                                <Button type="primary" onClick={applyFilters}>
                                    Apply Filters
                                </Button>
                            </Space>
                        </div>
                    }
                >
                    <Space direction="vertical" style={{ width: "100%" }} size="large">
                        <div>
                            <Text strong style={{ display: "block", marginBottom: 8 }}>
                                Date Range
                            </Text>
                            <RangePicker
                                style={{ width: "100%" }}
                                onChange={handleDateRangeChange}
                                value={
                                    filters.startDate && filters.endDate
                                        ? [
                                              dayjs(filters.startDate),
                                              dayjs(filters.endDate),
                                          ]
                                        : null
                                }
                            />
                        </div>

                        <div>
                            <Text strong style={{ display: "block", marginBottom: 8 }}>
                                Change Type
                            </Text>
                            <Select
                                mode="multiple"
                                style={{ width: "100%" }}
                                placeholder="All Types"
                                allowClear
                                value={filters.activityTypes}
                                onChange={(value) => {
                                    setFilters({ ...filters, activityTypes: value || [] });
                                }}
                            >
                                {ACTIVITY_TYPES.map((type) => (
                                    <Option key={type} value={type}>
                                        {formatChangeType(type)}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <div>
                            <Text strong style={{ display: "block", marginBottom: 8 }}>
                                Changed By
                            </Text>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="All Actors"
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                                }
                                value={filters.changedBy}
                                onChange={(value) => {
                                    setFilters({ ...filters, changedBy: value });
                                }}
                            >
                                {actors.length > 0 ? (
                                    actors.map((actor) => (
                                        <Option key={actor.actor_user_id || `system-${actor.actor_name}`} value={actor.actor_name}>
                                            {actor.actor_name}
                                        </Option>
                                    ))
                                ) : (
                                    <Option value="" disabled>Loading actors...</Option>
                                )}
                            </Select>
                        </div>

                        <div>
                            <Text strong style={{ display: "block", marginBottom: 8 }}>
                                Search
                            </Text>
                            <Input
                                placeholder="Search descriptions..."
                                value={filters.search}
                                onChange={(e) => {
                                    setFilters({ ...filters, search: e.target.value });
                                }}
                                allowClear
                            />
                        </div>
                    </Space>
                </Drawer>
            </Card>
        </div>
    );
};

export default ActivityLogs;

